import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc, setDoc, updateDoc, deleteDoc, collection, getDocs, addDoc, query, orderBy, limit } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';

// ============================================================
// VIRTUE KEY MAPPING — old format uses single letters
// ============================================================
const VIRTUE_SHORT = { discipline: 'D', attention: 'A', charity: 'C', inquiry: 'I' };
const VIRTUE_LONG = { D: 'discipline', A: 'attention', C: 'charity', I: 'inquiry' };

function useDebouncedSave(delay = 800) {
  const timer = useRef(null);
  return useCallback((fn) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(fn, delay);
  }, [delay]);
}

function parseStudentScores(allScores, studentName) {
  const result = {};
  for (const [dateStr, dateData] of Object.entries(allScores || {})) {
    const studentData = dateData?.[studentName];
    if (studentData) {
      result[dateStr] = {};
      for (const [shortKey, value] of Object.entries(studentData)) {
        if (VIRTUE_LONG[shortKey]) {
          const num = Number(value);
          result[dateStr][VIRTUE_LONG[shortKey]] = (!isNaN(num) && value !== null && value !== '') ? num : null;
        } else if (shortKey === 'E') {
          result[dateStr].absent = value;
        }
      }
    }
  }
  return result;
}

// ============================================================
// MASTER ROSTER HOOK — school-wide student list (admin-managed)
// Firestore: students/{studentId} = { name, house, gender, parentEmail, studentEmail }
// ============================================================
export function useMasterRoster() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  // Track auth so the roster fetch waits for sign-in. Without this, a brand-new
  // session fires getDocs before Firebase auth resolves, the request is denied,
  // and the load never retries — leaving the roster empty for the whole session.
  const [authedUid, setAuthedUid] = useState(() => auth.currentUser?.uid || null);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => setAuthedUid(u?.uid || null));
  }, []);

  useEffect(() => {
    if (!authedUid) {
      setStudents([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'students'));
        if (cancelled) return;
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setStudents(data);
      } catch (err) {
        console.error('Error loading master roster:', err);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [authedUid, refreshKey]);

  const addStudent = useCallback(async (studentData) => {
    const ref = collection(db, 'students');
    await addDoc(ref, {
      name: studentData.name.trim(),
      house: studentData.house || '',
      gender: studentData.gender || 'he',
      parentEmail: studentData.parentEmail || '',
      studentEmail: studentData.studentEmail || '',
      createdAt: new Date().toISOString(),
    });
    setRefreshKey(k => k + 1);
  }, []);

  const updateStudent = useCallback(async (studentId, updates) => {
    const ref = doc(db, 'students', studentId);
    await updateDoc(ref, updates);
    setRefreshKey(k => k + 1);
  }, []);

  const removeStudent = useCallback(async (studentId) => {
    const ref = doc(db, 'students', studentId);
    await deleteDoc(ref);
    setRefreshKey(k => k + 1);
  }, []);

  // Bulk import from old roster data
  const bulkImport = useCallback(async (names, house) => {
    for (const name of names) {
      if (!name.trim()) continue;
      // Check if already exists
      const existing = students.find(s => s.name.toLowerCase() === name.trim().toLowerCase());
      if (!existing) {
        await addDoc(collection(db, 'students'), {
          name: name.trim(),
          house: house || '',
          gender: 'he',
          parentEmail: '',
          studentEmail: '',
          createdAt: new Date().toISOString(),
        });
      }
    }
    setRefreshKey(k => k + 1);
  }, [students]);

  return { students, loading, addStudent, updateStudent, removeStudent, bulkImport, refresh: () => setRefreshKey(k => k + 1) };
}

// ============================================================
// TEACHER DATA HOOK — reads old HTML format + master roster
// ============================================================
export function useTeacherData(uid, masterStudents) {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadClasses = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const classesRef = collection(db, 'teachers', uid, 'classes');
      const snap = await getDocs(classesRef);
      const data = [];
      for (const d of snap.docs) {
        const raw = d.data();
        const roster = raw.roster || [];
        const classData = {
          id: d.id,
          name: raw.cls || raw.name || 'Unnamed Class',
          quarter: raw.quarter || 'Q4',
          roster,
          rawScores: raw.scores || {},
          students: roster.map((name, idx) => {
            // Look up in master roster for house/gender
            const master = (masterStudents || []).find(
              s => s.name.toLowerCase() === name.toLowerCase()
            );
            return {
              id: `roster_${idx}`,
              name,
              house: master?.house || raw.houses?.[name] || '',
              gender: master?.gender || 'he',
              scores: parseStudentScores(raw.scores, name),
            };
          }),
        };
        data.push(classData);
      }
      setClasses(data);
    } catch (err) {
      console.error('Error loading classes:', err);
    }
    setLoading(false);
  }, [uid, masterStudents]);

  useEffect(() => { loadClasses(); }, [loadClasses, refreshKey]);

  const addClass = useCallback(async (className) => {
    if (!uid || !className.trim()) return;
    const classesRef = collection(db, 'teachers', uid, 'classes');
    const newRef = doc(classesRef);
    await setDoc(newRef, { cls: className.trim(), quarter: 'Q4', roster: [], scores: {} });
    setRefreshKey(k => k + 1);
    return newRef.id;
  }, [uid]);

  const addStudentToClass = useCallback(async (classId, studentName) => {
    if (!uid || !studentName.trim()) return;
    const cls = classes.find(c => c.id === classId);
    if (!cls) return;
    if (cls.roster.includes(studentName.trim())) return; // Already in class
    const newRoster = [...cls.roster, studentName.trim()];
    const ref = doc(db, 'teachers', uid, 'classes', classId);
    await updateDoc(ref, { roster: newRoster });
    setRefreshKey(k => k + 1);
  }, [uid, classes]);

  const removeStudentFromClass = useCallback(async (classId, studentName) => {
    if (!uid) return;
    const cls = classes.find(c => c.id === classId);
    if (!cls) return;
    const newRoster = cls.roster.filter(n => n !== studentName);
    const ref = doc(db, 'teachers', uid, 'classes', classId);
    await updateDoc(ref, { roster: newRoster });
    setRefreshKey(k => k + 1);
  }, [uid, classes]);

  const saveDailyScore = useCallback((classId, studentId, date, virtueKey, score) => {
    if (!uid) return;
    const cls = classes.find(c => c.id === classId);
    if (!cls) return;
    const student = cls.students.find(s => s.id === studentId);
    if (!student) return;

    // Write immediately — NO debounce. Each score writes to a unique dot-path field
    // (e.g., scores.2026-04-11.StudentName.D) so concurrent writes never conflict.
    // The old debounce was cancelling previous saves, causing ~75% data loss when
    // a teacher clicks through scores quickly.
    const ref = doc(db, 'teachers', uid, 'classes', classId);
    if (virtueKey === 'absent') {
      updateDoc(ref, { [`scores.${date}.${student.name}.E`]: score }).catch(e => console.error('Score save failed:', e));
    } else {
      const shortKey = VIRTUE_SHORT[virtueKey];
      if (shortKey) {
        updateDoc(ref, { [`scores.${date}.${student.name}.${shortKey}`]: score }).catch(e => console.error('Score save failed:', e));
      }
    }

    setClasses(prev => prev.map(c => {
      if (c.id !== classId) return c;
      return {
        ...c,
        students: c.students.map(s => {
          if (s.id !== studentId) return s;
          const newScores = { ...s.scores };
          if (!newScores[date]) newScores[date] = {};
          if (virtueKey === 'absent') newScores[date].absent = score;
          else newScores[date][virtueKey] = score;
          return { ...s, scores: newScores };
        })
      };
    }));
  }, [uid, classes]);

  // Bulk save — single Firestore write for all changes (used by "All 4", "Copy Prev Day", etc.)
  // updates = [{ studentId, virtueKey, score }, ...]
  const saveBulkScores = useCallback(async (classId, date, updates) => {
    if (!uid || updates.length === 0) return;
    const cls = classes.find(c => c.id === classId);
    if (!cls) return;

    // Build a single Firestore field map
    const fieldUpdates = {};
    updates.forEach(({ studentId, virtueKey, score }) => {
      const student = cls.students.find(s => s.id === studentId);
      if (!student) return;
      if (virtueKey === 'absent') {
        fieldUpdates[`scores.${date}.${student.name}.E`] = score;
      } else {
        const shortKey = VIRTUE_SHORT[virtueKey];
        if (shortKey) fieldUpdates[`scores.${date}.${student.name}.${shortKey}`] = score;
      }
    });

    // Single Firestore write
    if (Object.keys(fieldUpdates).length > 0) {
      const ref = doc(db, 'teachers', uid, 'classes', classId);
      await updateDoc(ref, fieldUpdates);
    }

    // Update local state
    setClasses(prev => prev.map(c => {
      if (c.id !== classId) return c;
      return {
        ...c,
        students: c.students.map(s => {
          const myUpdates = updates.filter(u => u.studentId === s.id);
          if (myUpdates.length === 0) return s;
          const newScores = { ...s.scores };
          if (!newScores[date]) newScores[date] = {};
          myUpdates.forEach(u => {
            if (u.virtueKey === 'absent') newScores[date].absent = u.score;
            else newScores[date][u.virtueKey] = u.score;
          });
          return { ...s, scores: newScores };
        })
      };
    }));
  }, [uid, classes]);

  // Manual "Save All" — writes every score for a class+date in one Firestore write.
  // Acts as a safety net: even if individual auto-saves failed, this captures everything.
  const saveAllScoresForDay = useCallback(async (classId, date) => {
    if (!uid) return;
    const cls = classes.find(c => c.id === classId);
    if (!cls) return;

    const fieldUpdates = {};
    cls.students.forEach(student => {
      const dayScores = student.scores?.[date];
      if (!dayScores) return;
      Object.entries(dayScores).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (key === 'absent') {
          fieldUpdates[`scores.${date}.${student.name}.E`] = value;
        } else {
          const shortKey = VIRTUE_SHORT[key];
          if (shortKey) fieldUpdates[`scores.${date}.${student.name}.${shortKey}`] = value;
        }
      });
    });

    if (Object.keys(fieldUpdates).length > 0) {
      const ref = doc(db, 'teachers', uid, 'classes', classId);
      await updateDoc(ref, fieldUpdates);
    }
    return Object.keys(fieldUpdates).length;
  }, [uid, classes]);

  const deleteClass = useCallback(async (classId) => {
    if (!uid) return;
    await deleteDoc(doc(db, 'teachers', uid, 'classes', classId));
    setRefreshKey(k => k + 1);
  }, [uid]);

  return {
    classes, loading, loadClasses: () => setRefreshKey(k => k + 1),
    addClass, addStudentToClass, removeStudentFromClass, saveDailyScore, saveBulkScores, saveAllScoresForDay, deleteClass,
  };
}

// ============================================================
// NARRATIVE DATA HOOK
// ============================================================
export function useNarrativeData(uid) {
  const [narrativeConfig, setNarrativeConfig] = useState({
    teacherName: '', className: '', quarter: 'Q4', students: [],
  });
  const [loading, setLoading] = useState(true);
  const debouncedSave = useDebouncedSave();

  useEffect(() => {
    if (!uid) return;
    setLoading(true);
    getDoc(doc(db, 'teachers', uid, 'config', 'narrative')).then(snap => {
      if (snap.exists()) setNarrativeConfig(snap.data());
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [uid]);

  const saveNarrative = useCallback((data) => {
    if (!uid) return;
    setNarrativeConfig(data);
    debouncedSave(async () => {
      await setDoc(doc(db, 'teachers', uid, 'config', 'narrative'), data);
    });
  }, [uid, debouncedSave]);

  return { narrativeConfig, setNarrativeConfig: saveNarrative, loading };
}

// ============================================================
// HOUSE POINTS HOOK
// ============================================================
export function useHousePoints() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'housePointEntries'), orderBy('createdAt', 'desc'), limit(2000));
      const snap = await getDocs(q);
      setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addEntry = useCallback(async (entry) => {
    await addDoc(collection(db, 'housePointEntries'), {
      ...entry, points: Number(entry.points), createdAt: entry.createdAt || new Date().toISOString(),
    });
    await load();
  }, [load]);

  const bulkAddEntries = useCallback(async (entriesArr, onProgress) => {
    for (let i = 0; i < entriesArr.length; i += 50) {
      const chunk = entriesArr.slice(i, i + 50);
      const promises = chunk.map(entry =>
        addDoc(collection(db, 'housePointEntries'), {
          ...entry, points: Number(entry.points), createdAt: entry.createdAt || new Date().toISOString(),
        })
      );
      await Promise.all(promises);
      if (onProgress) onProgress(Math.min(i + 50, entriesArr.length), entriesArr.length);
    }
    if (onProgress) onProgress(entriesArr.length, entriesArr.length, 'Loading entries...');
    await load();
  }, [load]);

  const updateEntry = useCallback(async (id, updates) => {
    await updateDoc(doc(db, 'housePointEntries', id), updates);
    await load();
  }, [load]);

  const deleteEntry = useCallback(async (id) => {
    await deleteDoc(doc(db, 'housePointEntries', id));
    await load();
  }, [load]);

  const resetAll = useCallback(async () => {
    for (const entry of entries) {
      await deleteDoc(doc(db, 'housePointEntries', entry.id));
    }
    await load();
  }, [entries, load]);

  return { entries, loading, addEntry, bulkAddEntries, updateEntry, deleteEntry, resetAll, refresh: load };
}

// ============================================================
// CONDUCT ENTRIES HOOK
// ============================================================
export function useConductEntries() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'conductEntries'), orderBy('date', 'desc'), limit(500));
      const snap = await getDocs(q);
      setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addEntry = useCallback(async (entry) => {
    await addDoc(collection(db, 'conductEntries'), {
      ...entry, createdAt: new Date().toISOString(),
    });
    await load();
  }, [load]);

  return { entries, loading, addEntry, refresh: load };
}

// ============================================================
// ADMIN DATA HOOK — reads all teachers' data
// ============================================================
export function useAdminData() {
  const [allTeachers, setAllTeachers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const knownTeachers = [];
      const teacherIds = [
        'RfcdU5sf2Zhzj4aJTbfE7Iy5e5E2',
        'hvThHfEBFAY7VrG3YQ3djt0Icxi1',
        'xn858oNYT3XOP6afwXh9qnT06cx2'
      ];

      for (const uid of teacherIds) {
        const teacherData = { uid, classes: [] };
        try {
          const classSnap = await getDocs(collection(db, 'teachers', uid, 'classes'));
          for (const cDoc of classSnap.docs) {
            const raw = cDoc.data();
            teacherData.classes.push({
              id: cDoc.id,
              name: raw.cls || raw.name || 'Unnamed Class',
              students: (raw.roster || []).map((name, idx) => ({
                id: `roster_${idx}`, name,
                scores: parseStudentScores(raw.scores, name),
              })),
            });
          }
          knownTeachers.push(teacherData);
        } catch (e) {
          console.warn('Could not read teacher:', uid);
        }
      }
      setAllTeachers(knownTeachers);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  return { allTeachers, loading, refresh: loadAll };
}

// ============================================================
// ANNOUNCEMENTS HOOK — admin posts, everyone reads
// Firestore: announcements/{id} = { title, body, postedBy, postedAt, pinned }
// ============================================================
export function useAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'announcements'), orderBy('postedAt', 'desc'), limit(50));
      const snap = await getDocs(q);
      setAnnouncements(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      // If index not ready, try without ordering
      console.warn('Announcements ordered query failed, trying unordered:', err.message);
      try {
        const snap = await getDocs(collection(db, 'announcements'));
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => (b.postedAt || '').localeCompare(a.postedAt || ''));
        setAnnouncements(docs);
      } catch (err2) { console.error('Announcements load failed:', err2); }
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addAnnouncement = useCallback(async (data) => {
    await addDoc(collection(db, 'announcements'), {
      title: data.title,
      body: data.body,
      postedBy: data.postedBy || '',
      postedByName: data.postedByName || '',
      postedAt: new Date().toISOString(),
      pinned: data.pinned || false,
    });
    await load();
  }, [load]);

  const removeAnnouncement = useCallback(async (id) => {
    await deleteDoc(doc(db, 'announcements', id));
    await load();
  }, [load]);

  const togglePin = useCallback(async (id, currentPinned) => {
    await updateDoc(doc(db, 'announcements', id), { pinned: !currentPinned });
    await load();
  }, [load]);

  return { announcements, loading, addAnnouncement, removeAnnouncement, togglePin, refresh: load };
}

// ============================================================
// QUICK LINKS HOOK — admin manages, everyone sees
// Firestore: quickLinks/{id} = { label, url, order }
// ============================================================
export function useQuickLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'quickLinks'), orderBy('order', 'asc'));
      const snap = await getDocs(q);
      setLinks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addLink = useCallback(async (data) => {
    const maxOrder = links.reduce((max, l) => Math.max(max, l.order || 0), 0);
    await addDoc(collection(db, 'quickLinks'), {
      label: data.label, url: data.url, order: maxOrder + 1,
    });
    await load();
  }, [load, links]);

  const removeLink = useCallback(async (id) => {
    await deleteDoc(doc(db, 'quickLinks', id));
    await load();
  }, [load]);

  return { links, loading, addLink, removeLink, refresh: load };
}

// ============================================================
// SCHEDULE HOOK — single document stores entire schedule config
// Firestore: schedule/config = { schoolDay, rooms, teachers, studentGroups, classes, grid, published }
// Firestore: schedule/published = { ...same shape, set when admin hits Publish }
// ============================================================
export function useSchedule() {
  const [config, setConfig] = useState(null);
  const [published, setPublished] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const configSnap = await getDoc(doc(db, 'schedule', 'config'));
      if (configSnap.exists()) {
        const { config: migrated, changed } = splitGrade11And12(configSnap.data());
        setConfig(migrated);
        if (changed) await setDoc(doc(db, 'schedule', 'config'), migrated);
      } else {
        setConfig(getDefaultConfig());
      }

      const pubSnap = await getDoc(doc(db, 'schedule', 'published'));
      if (pubSnap.exists()) {
        const { config: migratedPub, changed } = splitGrade11And12(pubSnap.data());
        setPublished(migratedPub);
        if (changed) await setDoc(doc(db, 'schedule', 'published'), migratedPub);
      }
    } catch (err) { console.error('Error loading schedule:', err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveConfig = useCallback(async (newConfig) => {
    setConfig(newConfig);
    await setDoc(doc(db, 'schedule', 'config'), newConfig);
  }, []);

  const publish = useCallback(async (configToPublish) => {
    const pubData = { ...configToPublish, publishedAt: new Date().toISOString() };
    await setDoc(doc(db, 'schedule', 'published'), pubData);
    setPublished(pubData);
  }, []);

  return { config, published, loading, saveConfig, publish, refresh: load };
}

// ============================================================
// DOCUMENTS HOOK — embedded PDFs on Home tab
// Firestore: documents/{id} = { label, url, order }
// ============================================================
export function useDocuments() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'documents'), orderBy('order', 'asc'));
      const snap = await getDocs(q);
      setDocuments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      // Fallback if index not ready
      try {
        const snap = await getDocs(collection(db, 'documents'));
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => (a.order || 0) - (b.order || 0));
        setDocuments(docs);
      } catch (err2) { console.error('Documents load failed:', err2); }
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addDocument = useCallback(async (data) => {
    const maxOrder = documents.reduce((max, d) => Math.max(max, d.order || 0), 0);
    await addDoc(collection(db, 'documents'), {
      label: data.label, url: data.url, order: maxOrder + 1,
    });
    await load();
  }, [load, documents]);

  const removeDocument = useCallback(async (id) => {
    await deleteDoc(doc(db, 'documents', id));
    await load();
  }, [load]);

  return { documents, loading, addDocument, removeDocument, refresh: load };
}

// ============================================================
// CALENDARS HOOK — Google Calendar embeds on Home tab
// Firestore: calendars/{id} = { label, url, order }
// ============================================================
export function useCalendars() {
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'calendars'), orderBy('order', 'asc'));
      const snap = await getDocs(q);
      setCalendars(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      try {
        const snap = await getDocs(collection(db, 'calendars'));
        const cals = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        cals.sort((a, b) => (a.order || 0) - (b.order || 0));
        setCalendars(cals);
      } catch (err2) { console.error('Calendars load failed:', err2); }
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addCalendar = useCallback(async (data) => {
    const maxOrder = calendars.reduce((max, c) => Math.max(max, c.order || 0), 0);
    await addDoc(collection(db, 'calendars'), {
      label: data.label, url: data.url, order: maxOrder + 1,
    });
    await load();
  }, [load, calendars]);

  const removeCalendar = useCallback(async (id) => {
    await deleteDoc(doc(db, 'calendars', id));
    await load();
  }, [load]);

  return { calendars, loading, addCalendar, removeCalendar, refresh: load };
}

// ============================================================
// COMMAND CENTER HOOK — single document stores board data
// Firestore: commandCenter/data = { directors, completedMonths, boardDocs }
// ============================================================
export function useCommandCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'commandCenter', 'data'));
      if (snap.exists()) setData(snap.data());
      else setData({ directors: [], completedMonths: [], boardDocs: [] });
    } catch (err) { console.error('Error loading command center:', err); setData({ directors: [], completedMonths: [], boardDocs: [] }); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveData = useCallback(async (newData) => {
    setData(newData);
    await setDoc(doc(db, 'commandCenter', 'data'), newData);
  }, []);

  return { data, loading, saveData, refresh: load };
}

// ============================================================
// BUDGET HOOK — stores budget data
// Firestore: budget/data = { lineItems, scenarios, spending, publishedBudget }
// ============================================================
export function useBudget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'budget', 'data'));
      if (snap.exists()) setData(snap.data());
      else setData({ lineItems: [], scenarios: ['Scenario A'], spending: [], publishedBudget: null });
    } catch (err) { console.error('Error loading budget:', err); setData({ lineItems: [], scenarios: ['Scenario A'], spending: [], publishedBudget: null }); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveData = useCallback(async (newData) => {
    setData(newData);
    await setDoc(doc(db, 'budget', 'data'), newData);
  }, []);

  return { data, loading, saveData, refresh: load };
}

// ============================================================
// FINANCIAL PLANNING HOOK — 6-year projections, tuition model, salary schedule, financial aid
// Firestore: financialPlanning/data = { initialized, projections, revenue, tuition, salary, aid }
// ============================================================
export function useFinancialPlanning() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'financialPlanning', 'data'));
      if (snap.exists()) setData(snap.data());
      else setData({});
    } catch (err) {
      console.error('Error loading financial planning:', err);
      setData({});
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveData = useCallback(async (newData) => {
    setData(newData);
    await setDoc(doc(db, 'financialPlanning', 'data'), newData);
  }, []);

  return { data, loading, saveData, refresh: load };
}

// ============================================================
// SERVICE HOURS HOOK — individual documents per entry
// Firestore: serviceHours/{id} = { student, date, hours, organization, description, supervisor, supervisorContact, verification, createdAt }
// ============================================================
export function useServiceHours() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'serviceHours'));
      setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error('Service hours load failed:', err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addEntry = useCallback(async (data) => {
    await addDoc(collection(db, 'serviceHours'), data);
    await load();
  }, [load]);

  const updateEntry = useCallback(async (id, updates) => {
    await updateDoc(doc(db, 'serviceHours', id), updates);
    await load();
  }, [load]);

  const deleteEntry = useCallback(async (id) => {
    await deleteDoc(doc(db, 'serviceHours', id));
    await load();
  }, [load]);

  return { entries, loading, addEntry, updateEntry, deleteEntry, refresh: load };
}

// Splits the legacy combined "Grade 11/12" student group into separate
// Grade 11 and Grade 12 groups, and updates any class assigned to the
// combined group so it's assigned to both. Idempotent — returns the same
// config object untouched once there's no g11-12 group left.
function splitGrade11And12(config) {
  if (!config) return { config, changed: false };
  const groups = config.studentGroups || [];
  const combined = groups.find(g => g.id === 'g11-12');
  if (!combined) return { config, changed: false };

  const newGroups = groups.filter(g => g.id !== 'g11-12');
  if (!newGroups.some(g => g.id === 'g11')) {
    newGroups.push({ id: 'g11', name: 'Grade 11', color: combined.color || '#2E7D5B' });
  }
  if (!newGroups.some(g => g.id === 'g12')) {
    newGroups.push({ id: 'g12', name: 'Grade 12', color: '#B45309' });
  }

  const newClasses = (config.classes || []).map(cls => {
    if (!(cls.groupIds || []).includes('g11-12')) return cls;
    const ids = cls.groupIds.filter(id => id !== 'g11-12');
    if (!ids.includes('g11')) ids.push('g11');
    if (!ids.includes('g12')) ids.push('g12');
    return { ...cls, groupIds: ids };
  });

  return { config: { ...config, studentGroups: newGroups, classes: newClasses }, changed: true };
}

function getDefaultConfig() {
  return {
    schoolDay: {
      startTime: '08:00',
      endTime: '15:00',
      periodMinutes: 45,
      passingMinutes: 5,
      lunchAfterPeriod: 4,
      lunchMinutes: 30,
      periodMinutesOverrides: {},
    },
    rooms: [
      { id: 'r1', name: 'Room 1', capacity: 10, notes: 'Small classes only' },
      { id: 'r2', name: 'Room 2', capacity: 25, notes: '' },
      { id: 'r3', name: 'Room 3', capacity: 25, notes: '' },
      { id: 'r4', name: 'Room 4', capacity: 25, notes: '' },
    ],
    teachers: [],
    studentGroups: [
      { id: 'g9', name: 'Grade 9', color: '#1B3A5C' },
      { id: 'g10', name: 'Grade 10', color: '#8B2252' },
      { id: 'g11', name: 'Grade 11', color: '#2E7D5B' },
      { id: 'g12', name: 'Grade 12', color: '#B45309' },
    ],
    classes: [],
    grid: {}, // grid[periodIndex] = { classId, roomId }
    publishedAt: null,
  };
}

// ============================================================
// PTO ALLOTMENTS HOOK
// Firestore: ptoAllotments/{teacherId} = {
//   teacherId, displayName, contractType,
//   sick: number, vacation: number, bereavement: number,
//   updatedAt
// }
// ============================================================
export function usePTOAllotments() {
  const [allotments, setAllotments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await migratePTOUnitsToHours();
      const snap = await getDocs(collection(db, 'ptoAllotments'));
      setAllotments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error('PTO allotments load failed:', err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setAllotment = useCallback(async (teacherId, data) => {
    const payload = { ...data, teacherId, updatedAt: new Date().toISOString() };
    await setDoc(doc(db, 'ptoAllotments', teacherId), payload);
    setAllotments(prev => {
      const others = prev.filter(a => a.id !== teacherId);
      return [...others, { id: teacherId, ...payload }];
    });
  }, []);

  return { allotments, loading, setAllotment, refresh: load };
}

// ============================================================
// PTO REQUESTS HOOK
// Firestore: ptoRequests/{id} = {
//   teacherId, displayName, type ('sick'|'vacation'|'bereavement'),
//   startDate ('YYYY-MM-DD'), endDate, days (number),
//   reason, status ('pending'|'approved'|'denied'),
//   requestedAt, requestedBy (uid), submittedByAdmin (bool),
//   decidedAt, decidedBy (uid), decisionNote
// }
// ============================================================
export function usePTORequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'ptoRequests'));
      const sorted = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.requestedAt || '').localeCompare(a.requestedAt || ''));
      setRequests(sorted);
    } catch (err) { console.error('PTO requests load failed:', err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitRequest = useCallback(async (data) => {
    const payload = {
      status: 'pending',
      ...data,
      requestedAt: new Date().toISOString(),
    };
    const ref = await addDoc(collection(db, 'ptoRequests'), payload);
    const newReq = { id: ref.id, ...payload };
    setRequests(prev => [newReq, ...prev]);
    return ref.id;
  }, []);

  const decideRequest = useCallback(async (id, decision, decidedBy, note) => {
    const update = {
      status: decision,
      decidedAt: new Date().toISOString(),
      decidedBy: decidedBy || null,
      decisionNote: note || null,
    };
    await updateDoc(doc(db, 'ptoRequests', id), update);
    setRequests(prev => prev.map(r => r.id === id ? { ...r, ...update } : r));
  }, []);

  const deleteRequest = useCallback(async (id) => {
    await deleteDoc(doc(db, 'ptoRequests', id));
    setRequests(prev => prev.filter(r => r.id !== id));
  }, []);

  return { requests, loading, submitRequest, decideRequest, deleteRequest, refresh: load };
}

// ============================================================
// SUBSTITUTES HOOK — approved substitute teachers admin maintains.
// Firestore: substitutes/{id} = { id, name, email, phone, notes,
//   active, createdAt, updatedAt }
// ============================================================
export function useSubstitutes() {
  const [substitutes, setSubstitutes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'substitutes'));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setSubstitutes(data);
    } catch (err) { console.error('substitutes load failed:', err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addSubstitute = useCallback(async (data) => {
    const ref = doc(collection(db, 'substitutes'));
    const now = new Date().toISOString();
    const sub = {
      id: ref.id,
      name: (data.name || '').trim(),
      email: (data.email || '').trim().toLowerCase(),
      phone: (data.phone || '').trim(),
      notes: (data.notes || '').trim(),
      active: data.active !== false,
      createdAt: now,
      updatedAt: now,
    };
    await setDoc(ref, sub);
    await load();
    return ref.id;
  }, [load]);

  const updateSubstitute = useCallback(async (id, updates) => {
    const ref = doc(db, 'substitutes', id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const merged = {
      ...snap.data(),
      ...updates,
      email: updates.email !== undefined ? String(updates.email).trim().toLowerCase() : snap.data().email,
      updatedAt: new Date().toISOString(),
    };
    await setDoc(ref, merged);
    await load();
  }, [load]);

  const removeSubstitute = useCallback(async (id) => {
    await deleteDoc(doc(db, 'substitutes', id));
    await load();
  }, [load]);

  return { substitutes, loading, addSubstitute, updateSubstitute, removeSubstitute, refresh: load };
}

// ============================================================
// PTO UNITS MIGRATION — converts the bank from days to hours.
// Runs once, multiplies every ptoAllotment bucket and every
// ptoRequests.days value by 8 (one work day = 8 hours), then writes
// config/ptoUnits = { unit: 'hours', migratedAt } so it never runs
// again. After this completes, the system treats all PTO numbers as
// hours throughout. Idempotent.
// ============================================================
async function migratePTOUnitsToHours() {
  const flagRef = doc(db, 'config', 'ptoUnits');
  const flagSnap = await getDoc(flagRef);
  if (flagSnap.exists() && flagSnap.data().unit === 'hours') return false;

  const [allotmentsSnap, requestsSnap] = await Promise.all([
    getDocs(collection(db, 'ptoAllotments')),
    getDocs(collection(db, 'ptoRequests')),
  ]);

  const writes = [];
  for (const d of allotmentsSnap.docs) {
    const data = d.data();
    writes.push(setDoc(d.ref, {
      ...data,
      sick:        (Number(data.sick)        || 0) * 8,
      vacation:    (Number(data.vacation)    || 0) * 8,
      bereavement: (Number(data.bereavement) || 0) * 8,
      updatedAt: new Date().toISOString(),
    }));
  }
  for (const d of requestsSnap.docs) {
    const data = d.data();
    writes.push(setDoc(d.ref, { ...data, days: (Number(data.days) || 0) * 8 }));
  }

  // Same for the teacherDirectory.pto buckets so the new mgmt UI stays in sync.
  try {
    const dirSnap = await getDocs(collection(db, 'teacherDirectory'));
    for (const d of dirSnap.docs) {
      const data = d.data();
      const pto = data.pto || {};
      writes.push(setDoc(d.ref, {
        ...data,
        pto: {
          sick:        (Number(pto.sick)        || 0) * 8,
          vacation:    (Number(pto.vacation)    || 0) * 8,
          bereavement: (Number(pto.bereavement) || 0) * 8,
        },
        updatedAt: new Date().toISOString(),
      }));
    }
  } catch (err) { console.error('teacherDirectory PTO unit migration failed:', err); }

  await Promise.all(writes);
  await setDoc(flagRef, { unit: 'hours', migratedAt: new Date().toISOString(), convertedDocs: writes.length });
  return true;
}

// ============================================================
// TEACHER DIRECTORY HOOK — single source of truth for teacher records.
// Firestore: teacherDirectory/{id} = {
//   id, name, email (lowercased), uid (set on first sign-in match),
//   phone, title, classesTaught: string[], contractType ('ft'|'pt'|''),
//   defaultRoom, unavailable: [{ day, periods: [...] }, ...],
//   pto: { sick, vacation, bereavement },
//   permission ('teacher'|'admin'|'board'), active, createdAt, updatedAt
// }
//
// On first load this hook one-time-migrates from the legacy sources
// (schedule/config.teachers, ptoAllotments/*, config/roles) so no
// existing data is lost. Writes mirror back to the legacy locations
// while Schedule Builder, PTO Admin, and Access Control still consume
// them — that mirroring will be removed once those views read from
// teacherDirectory directly.
// ============================================================
const norm = (s) => (s || '').trim().toLowerCase();

async function runTeacherDirectoryMigration() {
  const flagRef = doc(db, 'config', 'teacherManagerMigration');
  const flagSnap = await getDoc(flagRef);
  if (flagSnap.exists() && flagSnap.data().done) return false;

  const [scheduleSnap, publishedSnap, allotmentsSnap, rolesSnap, directorySnap] = await Promise.all([
    getDoc(doc(db, 'schedule', 'config')),
    getDoc(doc(db, 'schedule', 'published')),
    getDocs(collection(db, 'ptoAllotments')),
    getDoc(doc(db, 'config', 'roles')),
    getDocs(collection(db, 'teacherDirectory')),
  ]);

  const existingIds = new Set(directorySnap.docs.map(d => d.id));
  const scheduleTeachers = (scheduleSnap.exists() ? scheduleSnap.data().teachers : null)
    || (publishedSnap.exists() ? publishedSnap.data().teachers : null) || [];
  const allotmentDocs = allotmentsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const roles = rolesSnap.exists() ? rolesSnap.data() : { admins: [], boardMembers: [] };
  const admins = new Set((roles.admins || []).map(norm));
  const board = new Set((roles.boardMembers || []).map(norm));

  const now = new Date().toISOString();
  const writes = [];
  const seenIds = new Set();

  for (const t of scheduleTeachers) {
    if (!t || !t.id) continue;
    seenIds.add(t.id);
    if (existingIds.has(t.id)) continue; // don't overwrite a manual edit
    const allotment = allotmentDocs.find(a => a.id === t.id) || {};
    const email = norm(t.email);
    const permission = email && admins.has(email) ? 'admin'
      : email && board.has(email) ? 'board' : 'teacher';
    writes.push(setDoc(doc(db, 'teacherDirectory', t.id), {
      id: t.id,
      name: t.name || '',
      email,
      uid: null,
      phone: '',
      title: '',
      classesTaught: [],
      contractType: t.type || '',
      defaultRoom: t.defaultRoom || '',
      unavailable: Array.isArray(t.unavailable) ? t.unavailable : [],
      pto: {
        sick: Number(allotment.sick) || 0,
        vacation: Number(allotment.vacation) || 0,
        bereavement: Number(allotment.bereavement) || 0,
      },
      permission,
      active: true,
      createdAt: now,
      updatedAt: now,
      migratedAt: now,
    }));
  }

  // Allotments that have no matching schedule teacher — bring them across too.
  for (const a of allotmentDocs) {
    if (seenIds.has(a.id) || existingIds.has(a.id)) continue;
    writes.push(setDoc(doc(db, 'teacherDirectory', a.id), {
      id: a.id,
      name: a.displayName || '',
      email: norm(a.email),
      uid: null,
      phone: '',
      title: '',
      classesTaught: [],
      contractType: a.contractType || '',
      defaultRoom: '',
      unavailable: [],
      pto: {
        sick: Number(a.sick) || 0,
        vacation: Number(a.vacation) || 0,
        bereavement: Number(a.bereavement) || 0,
      },
      permission: 'teacher',
      active: true,
      createdAt: now,
      updatedAt: now,
      migratedAt: now,
    }));
  }

  await Promise.all(writes);
  await setDoc(flagRef, { done: true, at: now, importedCount: writes.length });
  return writes.length > 0;
}

async function mirrorToLegacy(teacher) {
  // Schedule Builder still reads from schedule/config.teachers — keep it in sync.
  try {
    const ref = doc(db, 'schedule', 'config');
    const snap = await getDoc(ref);
    const cfg = snap.exists() ? snap.data() : null;
    if (cfg) {
      const list = Array.isArray(cfg.teachers) ? [...cfg.teachers] : [];
      const idx = list.findIndex(t => t && t.id === teacher.id);
      const entry = {
        id: teacher.id,
        name: teacher.name || '',
        type: teacher.contractType || 'ft',
        defaultRoom: teacher.defaultRoom || '',
        unavailable: teacher.unavailable || [],
      };
      if (idx >= 0) list[idx] = { ...list[idx], ...entry };
      else list.push(entry);
      await setDoc(ref, { ...cfg, teachers: list });
    }
  } catch (err) { console.error('Mirror to schedule failed:', err); }

  // PTO Admin reads ptoAllotments/{id}.
  try {
    await setDoc(doc(db, 'ptoAllotments', teacher.id), {
      teacherId: teacher.id,
      displayName: teacher.name || '',
      email: teacher.email || '',
      contractType: teacher.contractType || '',
      sick: Number(teacher.pto?.sick) || 0,
      vacation: Number(teacher.pto?.vacation) || 0,
      bereavement: Number(teacher.pto?.bereavement) || 0,
      updatedAt: new Date().toISOString(),
    });
  } catch (err) { console.error('Mirror to ptoAllotments failed:', err); }

  // Auth + Access Control read config/roles.
  if (teacher.email) {
    try {
      const ref = doc(db, 'config', 'roles');
      const snap = await getDoc(ref);
      const cur = snap.exists() ? snap.data() : { admins: [], boardMembers: [] };
      const admins = new Set((cur.admins || []).map(norm));
      const board = new Set((cur.boardMembers || []).map(norm));
      const e = norm(teacher.email);
      admins.delete(e); board.delete(e);
      if (teacher.permission === 'admin') admins.add(e);
      else if (teacher.permission === 'board') board.add(e);
      await setDoc(ref, { admins: [...admins], boardMembers: [...board] });
    } catch (err) { console.error('Mirror to roles failed:', err); }
  }
}

async function removeFromLegacy(teacher) {
  try {
    const ref = doc(db, 'schedule', 'config');
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const cfg = snap.data();
      const list = (cfg.teachers || []).filter(t => t && t.id !== teacher.id);
      await setDoc(ref, { ...cfg, teachers: list });
    }
  } catch (err) { console.error('Remove from schedule failed:', err); }
  try { await deleteDoc(doc(db, 'ptoAllotments', teacher.id)); } catch (_) {}
  if (teacher.email) {
    try {
      const ref = doc(db, 'config', 'roles');
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const cur = snap.data();
        const e = norm(teacher.email);
        await setDoc(ref, {
          admins: (cur.admins || []).filter(a => norm(a) !== e),
          boardMembers: (cur.boardMembers || []).filter(a => norm(a) !== e),
        });
      }
    } catch (_) {}
  }
}

export function useTeacherDirectory() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'teacherDirectory'));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setTeachers(data);
    } catch (err) { console.error('teacherDirectory load failed:', err); }
    setLoading(false);
  }, []);

  useEffect(() => {
    (async () => {
      setMigrating(true);
      try { await runTeacherDirectoryMigration(); }
      catch (err) { console.error('Teacher migration failed:', err); }
      setMigrating(false);
      await load();
    })();
  }, [load]);

  const addTeacher = useCallback(async (data) => {
    const ref = doc(collection(db, 'teacherDirectory'));
    const now = new Date().toISOString();
    const teacher = {
      id: ref.id,
      name: data.name || '',
      email: norm(data.email),
      uid: null,
      phone: data.phone || '',
      title: data.title || '',
      classesTaught: Array.isArray(data.classesTaught) ? data.classesTaught : [],
      contractType: data.contractType || 'ft',
      defaultRoom: data.defaultRoom || '',
      unavailable: data.unavailable || [],
      pto: {
        sick: Number(data.pto?.sick) || 0,
        vacation: Number(data.pto?.vacation) || 0,
        bereavement: Number(data.pto?.bereavement) || 0,
      },
      permission: data.permission || 'teacher',
      active: data.active !== false,
      createdAt: now,
      updatedAt: now,
    };
    await setDoc(ref, teacher);
    await mirrorToLegacy(teacher);
    await load();
    return ref.id;
  }, [load]);

  const updateTeacher = useCallback(async (id, updates) => {
    const ref = doc(db, 'teacherDirectory', id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const current = snap.data();
    const merged = {
      ...current,
      ...updates,
      email: updates.email !== undefined ? norm(updates.email) : current.email,
      pto: { ...current.pto, ...(updates.pto || {}) },
      id,
      updatedAt: new Date().toISOString(),
    };
    await setDoc(ref, merged);
    await mirrorToLegacy(merged);
    await load();
  }, [load]);

  const removeTeacher = useCallback(async (id) => {
    const ref = doc(db, 'teacherDirectory', id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const teacher = snap.data();
    await deleteDoc(ref);
    await removeFromLegacy(teacher);
    await load();
  }, [load]);

  return { teachers, loading, migrating, addTeacher, updateTeacher, removeTeacher, refresh: load };
}

// ============================================================
// CADENCE TASKS HOOK
// Firestore: cadenceTasks/{id} = {
//   id, month ('jul'..'jun'), title, description,
//   defaultRole, assignedTo, category, recurring,
//   dueDate ('YYYY-MM-DD'), status ('pending'|'complete'|'delayed'|'na'),
//   note, completedAt, completedBy
// }
// Re-seeding is idempotent — only inserts tasks whose id isn't already
// in the database, so the admin can safely click "Load defaults" twice.
// ============================================================
export function useCadenceTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'cadenceTasks'));
      setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error('Cadence tasks load failed:', err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateTask = useCallback(async (id, patch) => {
    await updateDoc(doc(db, 'cadenceTasks', id), patch);
    setTasks(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
  }, []);

  const upsertTask = useCallback(async (id, data) => {
    await setDoc(doc(db, 'cadenceTasks', id), data, { merge: true });
    setTasks(prev => {
      const others = prev.filter(t => t.id !== id);
      return [...others, { id, ...data }];
    });
  }, []);

  const addCustomTask = useCallback(async (data) => {
    const ref = await addDoc(collection(db, 'cadenceTasks'), data);
    setTasks(prev => [...prev, { id: ref.id, ...data }]);
    return ref.id;
  }, []);

  const deleteTask = useCallback(async (id) => {
    await deleteDoc(doc(db, 'cadenceTasks', id));
    setTasks(prev => prev.filter(t => t.id !== id));
  }, []);

  // Bulk seed: only inserts tasks whose id isn't already present.
  const seedDefaults = useCallback(async (seedList) => {
    const existing = new Set(tasks.map(t => t.id));
    const toAdd = seedList.filter(t => !existing.has(t.id));
    for (const t of toAdd) {
      const payload = {
        month: t.month,
        title: t.title,
        description: t.description || '',
        defaultRole: t.defaultRole || '',
        assignedTo: t.defaultRole || '',
        category: t.category || 'operational',
        recurring: !!t.recurring,
        dueDate: t.defaultDueDate || '',
        status: 'pending',
        note: '',
        completedAt: null,
        completedBy: null,
        seedId: t.id,
      };
      await setDoc(doc(db, 'cadenceTasks', t.id), payload);
    }
    await load();
    return toAdd.length;
  }, [tasks, load]);

  return { tasks, loading, updateTask, upsertTask, addCustomTask, deleteTask, seedDefaults, refresh: load };
}


